<template>
  <section>
    <div class="popups">
      <b-sidebar
        v-model="showFilters"
        right
        shadow
        backdrop
        bg-variant="white"
        no-header
        sidebar-class="sidebar-lg"
      >
        <template>
          <div class="container sidebar-header">
            <b-row>
              <div class="col">
                <b-button
                  variant="primary"
                  @click="
                    applyFilters();
                    pagination.page = 1;
                    loadItems();
                    showFilters = false;
                  "
                  >Apply</b-button
                >
              </div>
              <div class="col-auto text-right">
                <b-button
                  type="reset"
                  variant="outline-danger"
                  @click="resetFormFilters()"
                >
                  Reset
                </b-button>
                <b-button
                  variant="outline-secondary"
                  class="ml-1"
                  @click="showFilters = false; formFilters = { ...itemsFilters };"
                >
                  Cancel
                </b-button>
              </div>
            </b-row>
          </div>

          <div class="px-3 py-2">
            <b-form-group label="Brand:">
              <b-v-select
                v-model="formFilters.brandId"
                :searchable="true"
                :options="brandsFilter"
              />
            </b-form-group>
            <b-form-group label="Warehouse:">
              <b-v-select
                v-model="formFilters.warehouseId"
                :searchable="true"
                :options="warehousesFilter"
              />
            </b-form-group>
          </div>
        </template>
      </b-sidebar>
    </div>

    <div class="header">
      <div class="header-body">
        <b-row class="align-items-end">
          <div class="col">
            <h1 class="header-title">Fulfillment Batches</h1>
          </div>
          <div class="col-auto d-flex align-items-center">
            <b-input-group class="mr-2" style="width: 200px;">
              <b-form-input
                v-model="formFilters.code"
                placeholder="Exact code..."
                :disabled="loadingItems"
                @keyup.enter.native="applyFilters(); pagination.page = 1; loadItems();"
              />
              <b-input-group-append>
                <b-button
                  variant="outline-secondary"
                  :disabled="loadingItems"
                  @click="applyFilters(); pagination.page = 1; loadItems();"
                >
                  <i class="fa fa-search"></i>
                </b-button>
              </b-input-group-append>
            </b-input-group>
            <b-input-group class="mr-2" style="width: 200px;">
              <b-form-input
                v-model="formFilters.name"
                placeholder="Name contains..."
                :disabled="loadingItems"
                @keyup.enter.native="applyFilters(); pagination.page = 1; loadItems();"
              />
              <b-input-group-append>
                <b-button
                  variant="outline-secondary"
                  :disabled="loadingItems"
                  @click="applyFilters(); pagination.page = 1; loadItems();"
                >
                  <i class="fa fa-search"></i>
                </b-button>
              </b-input-group-append>
            </b-input-group>
            <b-button
              variant="primary"
              @click="showFilters = !showFilters"
              :disabled="loadingItems"
            >
              <i class="fa fa-filter mr-2"></i>
              Filters
            </b-button>
          </div>
        </b-row>
      </div>
    </div>

    <TableListing
      :loading-page="loadingItems"
      :failed-loading="failedLoadItems"
      head-variant="dark"
      :items.sync="items"
      :fields.sync="fields"
      :pagination.sync="pagination"
      :options.sync="tableOptions"
      v-on:pagination-changed="loadItems"
    >
      <template v-slot:cell(code)="data">
        <strong>{{ data.item.code }}</strong>
      </template>

      <template v-slot:cell(name)="data">
        <span v-if="data.item.name">{{ data.item.name }}</span>
        <span v-else class="text-muted">—</span>
      </template>

      <template v-slot:cell(brandId)="data">
        <span v-if="myBrands && myBrands.find(b => b.id === data.item.brandId)">
          {{ myBrands.find(b => b.id === data.item.brandId).name }}
        </span>
        <span v-else class="text-muted">{{ data.item.brandId }}</span>
      </template>

      <template v-slot:cell(warehouseId)="data">
        <span v-if="myWarehouses && myWarehouses.find(w => w.id === data.item.warehouseId)">
          {{ myWarehouses.find(w => w.id === data.item.warehouseId).name }}
        </span>
        <span v-else class="text-muted">{{ data.item.warehouseId }}</span>
      </template>

      <template v-slot:cell(totalOrders)="data">
        <span class="badge badge-secondary">{{ data.item.totalOrders }}</span>
      </template>

      <template v-slot:cell(ordersByFulfillmentStatus)="data">
        <div v-if="data.item.ordersByFulfillmentStatus && Object.keys(data.item.ordersByFulfillmentStatus).length">
          <div
            v-for="(count, status) in data.item.ordersByFulfillmentStatus"
            :key="status"
            class="d-inline-flex align-items-center mr-1 mb-1"
          >
            <span
              class="badge"
              :class="fulfillmentStatusBadgeClass(status)"
            >
              {{ fulfillmentStatusLabel(status) }}: {{ count }}
            </span>
            <router-link
              :to="{
                name: 'Fulfillment.StorefrontOrders.Index',
                query: { fulfillmentBatchSearch: data.item.code, fulfillmentStatus: status }
              }"
              class="ml-1"
              :title="'Filter: ' + fulfillmentStatusLabel(status)"
            >
              <i class="fa fa-external-link" style="font-size: 0.75em;"></i>
            </router-link>
          </div>
        </div>
        <span v-else class="text-muted">—</span>
      </template>

      <template v-slot:cell(createDateTimeUtc)="data">
        <span v-if="data.item.createDateTimeUtc">
          {{ data.item.createDateTimeUtc | moment('MMM Do YY, h:mm a') }}
        </span>
      </template>

      <template v-slot:cell(id)="data">
        <b-button
          size="sm"
          variant="outline-primary"
          :to="{
            name: 'Fulfillment.StorefrontOrders.Index',
            query: { fulfillmentBatchSearch: data.item.code }
          }"
        >
          <i class="fa fa-list mr-1"></i>
          View Orders
        </b-button>
      </template>
    </TableListing>
  </section>
</template>

<script>
import TableListing from '@/views/components/TableListing.vue';
import { crudMixinGen, myCommonDataMixin } from '@/helpers/utils/vueMixins.js';

export default {
  name: 'Fulfillment.Batches.Index',
  mixins: [crudMixinGen('fulfillmentBatches'), myCommonDataMixin],
  components: {
    TableListing
  },

  data: () => ({
    fields: [
      { key: 'code', label: 'Batch Code', sortable: false },
      { key: 'name', label: 'Name / Description', sortable: false },
      { key: 'brandId', label: 'Brand', sortable: false },
      { key: 'warehouseId', label: 'Warehouse', sortable: false },
      { key: 'totalOrders', label: 'Total Orders', sortable: false },
      { key: 'ordersByFulfillmentStatus', label: 'Orders by Status', sortable: false },
      { key: 'createDateTimeUtc', label: 'Created', sortable: false },
      { key: 'id', label: 'Actions', sortable: false }
    ],

    fulfillmentStatusOptions: [
      { value: 'NotStarted', text: 'Not Started', badge: 'badge-secondary' },
      { value: 'PickTicketPending', text: 'Pick Ticket Pending', badge: 'badge-light' },
      { value: 'PickingInProgress', text: 'Picking', badge: 'badge-info' },
      { value: 'NotAllocated', text: 'Not Allocated', badge: 'badge-warning' },
      { value: 'PendingFulfillment', text: 'Ready to Label', badge: 'badge-primary' },
      { value: 'WaitingConfirmation', text: 'Fully Labeled', badge: 'badge-warning' },
      { value: 'ScheduledAutoconfirm', text: 'Sched. Autoconfirm', badge: 'badge-info' },
      { value: 'Confirmed', text: 'Confirmed', badge: 'badge-success' }
    ]
  }),

  mounted() {
    this.loadItems();
  },

  methods: {
    fulfillmentStatusLabel(status) {
      var opt = this.fulfillmentStatusOptions.find(function(o) { return o.value === status; });
      return opt ? opt.text : status;
    },

    fulfillmentStatusBadgeClass(status) {
      var opt = this.fulfillmentStatusOptions.find(function(o) { return o.value === status; });
      return opt ? opt.badge : 'badge-secondary';
    }
  }
};
</script>
